import { useEffect, useState } from 'react'
import { api } from '../api.js'

const COULEURS = ['#1B3B8F', '#0E5B4A', '#8A3324', '#5B2B82', '#0B5566', '#7A1F3D', '#2C4A22', '#3F3F46']
const BLOCS = {
  hero: ["Bandeau d'accueil", 'Le titre et la phrase qui accueillent les habitants.'],
  alerte: ["Bandeau d'alerte", 'Message temporaire bien visible : canicule, coupure, intempéries.'],
  search: ['Barre de recherche', 'Pour trouver une démarche en tapant deux mots.'],
  popular: ['Démarches en avant', 'Les plus demandées, en gros, tout en haut.'],
  categories: ['Toutes les démarches', 'La liste complète, rangée par thème.'],
  suivi: ['Suivre ma demande', 'Encart qui renvoie vers le suivi des dossiers.'],
  infos: ['Horaires et contact', 'Les informations pratiques de la mairie.'],
}

// Contraste : la mairie choisit une couleur, on refuse d'aller sous 4,5 pour 1.
const hex2rgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16))
const lum = (h) => {
  const s = hex2rgb(h).map((v) => (v /= 255) <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4)
  return 0.2126 * s[0] + 0.7152 * s[1] + 0.0722 * s[2]
}
const contraste = (a, b) => (Math.max(lum(a), lum(b)) + 0.05) / (Math.min(lum(a), lum(b)) + 0.05)
const assombrir = (h, p) => '#' + hex2rgb(h).map((v) =>
  Math.round(v * (1 + p)).toString(16).padStart(2, '0')).join('')

function corriger(couleur) {
  let c = couleur, corrige = false, garde = 0
  while (contraste(c, '#ffffff') < 4.5 && garde++ < 24) { c = assombrir(c, -0.06); corrige = true }
  return { couleur: c, corrige, ratio: Math.round(contraste(c, '#ffffff') * 10) / 10,
           origine: Math.round(contraste(couleur, '#ffffff') * 10) / 10 }
}

export default function Apparence() {
  const [site, setSite] = useState(null)
  const [glisse, setGlisse] = useState(null)

  useEffect(() => { api.site().then(setSite) }, [])
  if (!site) return <p className="chargement">Chargement…</p>

  const verdict = corriger(site.theme.primary || '#1B3B8F')

  const sauver = (patch) => {
    const nouveau = { ...site, ...patch }
    setSite(nouveau)
    api.saveSite(patch)
  }
  const majBlocs = (blocks) => sauver({ blocks })

  function deposer(cible) {
    if (glisse === null || glisse === cible) return
    const blocks = [...site.blocks]
    const [b] = blocks.splice(glisse, 1)
    blocks.splice(cible, 0, b)
    setGlisse(null)
    majBlocs(blocks)
  }

  return (
    <>
      <h1>Apparence du portail</h1>
      <p className="chapo">Une couleur, un ordre de blocs. Le reste est calculé pour rester lisible
        par tout le monde.</p>

      <div className="deux">
        <section>
          <div className="carte">
            <h2>Couleur</h2>
            <div className="couleurs">
              {COULEURS.map((c) => (
                <button key={c} style={{ background: c }} aria-label={`Couleur ${c}`}
                        className={site.theme.primary === c ? 'on' : ''}
                        onClick={() => sauver({ theme: { ...site.theme, primary: c } })} />
              ))}
              <label className="perso">
                <input type="color" value={site.theme.primary || '#1B3B8F'}
                       onChange={(e) => sauver({ theme: { ...site.theme, primary: e.target.value } })} />
                <span aria-hidden="true">+</span>
                <span className="sr">Couleur personnalisée</span>
              </label>
            </div>
            <p className={`contraste ${verdict.corrige ? 'attention' : 'bon'}`} role="status">
              {verdict.corrige
                ? <>La teinte choisie n'était pas assez contrastée sur fond blanc ({verdict.origine} pour 1).
                    Le portail utilise <strong>{verdict.couleur}</strong>, qui atteint {verdict.ratio} pour 1
                    et respecte le référentiel d'accessibilité.</>
                : <>Contraste de {verdict.ratio} pour 1 sur fond blanc, au-dessus du minimum légal de 4,5.</>}
            </p>
          </div>

          <div className="carte">
            <h2>Blocs de la page d'accueil</h2>
            <p className="aide">Glissez pour réordonner, ou utilisez les flèches.</p>
            <ul className="blocs">
              {site.blocks.map((b, i) => (
                <li key={b.id} draggable className={b.on ? '' : 'off'}
                    onDragStart={() => setGlisse(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => deposer(i)}>
                  <span className="poignee" aria-hidden="true">⠿</span>
                  <span className="lib">
                    <strong>{BLOCS[b.type]?.[0] || b.type}</strong>
                    <span>{BLOCS[b.type]?.[1]}</span>
                  </span>
                  <span className="fleches">
                    <button aria-label="Monter" disabled={i === 0} onClick={() => {
                      const blocks = [...site.blocks]
                      ;[blocks[i - 1], blocks[i]] = [blocks[i], blocks[i - 1]]
                      majBlocs(blocks)
                    }}>▲</button>
                    <button aria-label="Descendre" disabled={i === site.blocks.length - 1} onClick={() => {
                      const blocks = [...site.blocks]
                      ;[blocks[i + 1], blocks[i]] = [blocks[i], blocks[i + 1]]
                      majBlocs(blocks)
                    }}>▼</button>
                  </span>
                  <label className="bascule">
                    <input type="checkbox" checked={b.on} onChange={(e) => {
                      majBlocs(site.blocks.map((x) => x.id === b.id ? { ...x, on: e.target.checked } : x))
                    }} />
                    <span className="sr">Afficher ce bloc</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <aside className="carte apercu">
          <h2>Aperçu</h2>
          <div className="ecran" style={{ '--pri': verdict.couleur }}>
            <div className="faux-nav">{site.theme.logo_text || 'MM'}</div>
            <div className="faux-hero">
              <strong>{site.blocks.find((b) => b.type === 'hero')?.title}</strong>
              <p>{site.blocks.find((b) => b.type === 'hero')?.lede}</p>
            </div>
            <div className="faux-corps">
              {site.blocks.filter((b) => b.on && b.type !== 'hero').map((b) => (
                <div className="faux-bloc" key={b.id}>{BLOCS[b.type]?.[0]}</div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </>
  )
}
